import { createReminder, updateReminder } from '@/businessRepository/reminderTable/driver';
import { getReminderByDate } from '@/businessRepository/reminderTable/dataAccess';
import { IResponse } from '@/appLogic/leanpath/interfaces';
import { getUserId } from '@/awsCodes/awsUtils';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { ORDER_REMINDER_SUBJECT, buildOrderReminderBody } from '@/orderTableModule/constants/reminderMail';

/**
 * リマインドメールデータの型定義
 */
export interface ReminderMailData {
  /** 発注ID */
  orderId: string;
  /** メモ内容 */
  message: string;
  /** リマインダー日時 */
  reminderDate: string;
  /** リマインド受信者 */
  recipients: string[];
}

/**
 * リマインドメール設定を保存するUseCase
 */
export class SaveReminderMailSettingsUseCase {
  /**
   * リマインドメール設定を作成・更新する
   * @param reminderMailData - リマインドメール設定データ
   * @returns APIレスポンス（dataIdを含む）
   */
  async execute(reminderMailData: ReminderMailData): Promise<IResponse & { dataId?: string }> {
    const { message, reminderDate, recipients } = reminderMailData;
    const userId = getUserId();

    // 送信元設定
    const mailSender = 'order_remind@lean-path.com';
    const subject = ORDER_REMINDER_SUBJECT;

    // 日付をYYYYMMDD形式に変換
    // UTC解釈による日付ズレを避けるため、ローカルのDateとして解釈
    const date = DateService.getDate(reminderDate);
    const iDate = DateService.convertDateToIDate(date);
    const dateString = DateService.convertIYearMonthDayToYYYYMMDD(iDate);

    try {
      // 既存のリマインダーがあるかチェック
      const existingReminders = (await getReminderByDate(dateString)).filter((reminder) => reminder.mailTo[0] === userId);

      let response: IResponse;

      const mailBody = buildOrderReminderBody(message);

      if (existingReminders.length === 0) {
        // 新規作成
        response = await createReminder(dateString, recipients, mailSender, subject, mailBody);
      } else {
        // 既存更新
        const existingReminder = existingReminders[0];
        response = await updateReminder(existingReminder.dataId, dateString, recipients, mailSender, subject, mailBody);
      }

      // dataIdを返すために、作成・更新後に再取得
      if (response.success) {
        const updatedReminders = (await getReminderByDate(dateString)).filter((reminder) => reminder.mailTo[0] === userId);

        if (updatedReminders.length > 0) {
          return {
            ...response,
            dataId: updatedReminders[0].dataId,
          };
        }
      }

      return response;
    } catch (error) {
      console.error('Failed to save reminder mail settings:', error);
      return {
        success: false,
        result: null,
        code: 500,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }
}
